import { useSyncExternalStore } from "react"
import { currentSunsetWeek } from "./sunset-hint-content"

// Tracks which weekly sunset hint the creator has already dismissed, scoped to
// the account. The stored value is the dismissed week's `start` string, so the
// hint reappears as soon as a new week takes over (its `start` differs). Backed
// by a shared external store — mirroring use-vertical-nav-optin — so dismissing
// in one mounted anchor immediately updates any other.
const storageKey = () =>
  `vertical-nav-sunset-hint:${window.app?.user?.account_id}`

const listeners = new Set()

const readState = () => {
  try {
    return window.localStorage.getItem(storageKey())
  } catch {
    return null
  }
}

const writeState = value => {
  try {
    window.localStorage.setItem(storageKey(), value)
  } catch {
    // Ignore write failures (e.g. storage disabled); the hint still behaves
    // correctly for this page load.
  }
  listeners.forEach(listener => listener())
}

const subscribe = listener => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Drives the weekly "the old navigation is going away" coachmark. Returns the
 * current week's copy and whether it should be shown (not yet dismissed this
 * week). Callers still gate on eligibility (on the old nav, desktop, etc.) —
 * this hook only owns the week rotation and per-week dismissal.
 */
export function useSunsetHint() {
  const dismissedStart = useSyncExternalStore(subscribe, readState, () => null)
  const week = currentSunsetWeek()

  return {
    open: week != null && dismissedStart !== week.start,
    title: week?.title,
    description: week?.description,
    dismiss: () => {
      if (week) writeState(week.start)
    }
  }
}
